import { t } from "@travily/i18n";
import { ActionMenu } from "@ui/components/ActionMenu";
import { Icon } from "@ui/components/Icon";
import { OutlineButton } from "@ui/components/OutlineButton";
import type { FriendshipStatus } from "@/components/FriendshipButton/FriendshipButton";

export interface ProfileActionMenuProps {
  canEdit: boolean;
  friendshipStatus: FriendshipStatus;
  messageHref?: string;
  onAboutAccount?: () => void;
  onShareProfile?: () => void;
  onUnfriend?: () => void;
  /** Opens the account's friends list modal. Omit to drop the row (restricted profiles). */
  onViewFriends?: () => void;
}

/**
 * The "..." action menu on a profile (individual / family / community). The
 * actions are identical across profile types — only `canEdit` / `friendshipStatus`
 * and the handlers vary — so the three ShowViews share this one component. The
 * "start chat" row is mobile-only (desktop surfaces it via the ActionBar).
 */
export function ProfileActionMenu({
  canEdit,
  friendshipStatus,
  messageHref,
  onAboutAccount,
  onShareProfile,
  onUnfriend,
  onViewFriends,
}: ProfileActionMenuProps) {
  return (
    <ActionMenu
      items={[
        !canEdit && {
          id: "chat",
          label: t("common.profile.start_chat"),
          icon: <Icon name="paperPlane" />,
          href: messageHref,
          mobileOnly: true,
        },
        onViewFriends && {
          id: "friends",
          label: t("common.profile.friends"),
          icon: <Icon name="people" />,
          onAction: onViewFriends,
        },
        {
          id: "about",
          label: t("common.profile.about_account"),
          icon: <Icon name="infoCircle" />,
          onAction: onAboutAccount,
        },
        {
          id: "share",
          label: t("common.profile.share_profile"),
          icon: <Icon name="share" />,
          onAction: onShareProfile,
        },
        !canEdit &&
          friendshipStatus === "approved" && {
            id: "unfriend",
            label: t("common.profile.unfriend"),
            icon: <Icon name="userX" />,
            onAction: onUnfriend,
            destructive: true,
          },
      ]}
      placement="bottom end"
      trigger={
        <OutlineButton aria-label={t("common.actions.more")} shape="circle" size="36">
          <Icon name="more" />
        </OutlineButton>
      }
    />
  );
}
